import React, { useMemo } from 'react';

import { ValidatorMap } from 'ia-react-core/~/interfaces/ValidatorMap';
import { MessageMap } from 'ia-react-core/~/interfaces/MessageMap';
import { ErrorMap } from 'ia-react-core/~/interfaces/ErrorMap';

import FormErrorMessage from '../FormErrorMessage';
import { StyledMessageWrapper, StyledMessage } from './ControlErrorMessages.styles';
import SafeHtml from '../../security/SafeHtml';
import useValidationMessages from '../../../hooks/_useValidationMessages';
import { MessageProps } from '../Message';

export interface ControlErrorMessagesProps<Value = unknown> extends MessageProps {
  /** An array of error messages. */
  errors?: ErrorMap;
  /** An array of warnings messages. */
  warnings?: ErrorMap;
  /** An array of warnings messages. */
  cautions?: ErrorMap;
  /** Name of the control */
  name?: string;
  /** The validators used to generate the errors. */
  validators?: ValidatorMap<Value>;
  /** Messages to extend the messages obtained from the ValidationContext */
  messages?: MessageMap<Value>;
  /** Maximum number of rows a message can take */
  maxMessageRows?: number;
  /** Value of the control. */
  value?: Value;
}

const ControlErrorMessagesDefaultProps = {
  errors: {},
  warnings: {},
  cautions: {},
  messages: {},
  validators: {},
  maxMessageRows: 3,
};

// t is really just to force message to be reloaded when the language changes
const ControlErrorMessages = <Value, >({
  name, value, errors, warnings, cautions, validators, messages, maxMessageRows, ...props
}: ControlErrorMessagesProps<Value>) => {
  const errorMessages = useValidationMessages<Value>({ value, messages, errors, validators });
  const warningsMessages = useValidationMessages<Value>({ value, messages, errors: warnings, validators });
  const cautionsMessages = useValidationMessages<Value>({ value, messages, errors: cautions, validators });

  const actualMessages = useMemo(() => {
    if (errorMessages.length) {
      return errorMessages;
    }
    if (warningsMessages.length) {
      return warningsMessages;
    }
    return cautionsMessages;
  }, [errorMessages, warningsMessages, cautionsMessages]);

  const errorType = useMemo(() => {
    if (errorMessages.length) return 'error';
    if (warningsMessages.length) return 'warning';
    return 'caution';
  }, [errorMessages.length, warningsMessages.length]);

  if (!errorMessages.length && !warningsMessages.length && !cautionsMessages.length) {
    return null;
  }

  return (
    <FormErrorMessage
      data-testid="ControlErrorMessages"
      {...props}
      data-test-for={name}
      errorType={errorType}
    >
      <StyledMessageWrapper>
        {actualMessages.map(({ type, message }) => (
          <StyledMessage
            key={type}
            data-test-message-type={type}
            maxMessageRows={maxMessageRows}
          >
            {typeof message === 'string' && <SafeHtml>{message }</SafeHtml>}
            {typeof message !== 'string' && message}
          </StyledMessage>
        ))}
      </StyledMessageWrapper>
    </FormErrorMessage>
  );
};

ControlErrorMessages.defaultProps = ControlErrorMessagesDefaultProps;

export default ControlErrorMessages;
